import { defineComponent as _defineComponent } from 'vue'
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { ref, getCurrentInstance, computed } from 'vue';

import { useBilling } from '@/components/Billing/Billing.composables';
import BillingService from '@/components/Billing/billing-service';
import { DATE_FORMAT_DISPLAY } from '@/components/Billing/billing-service.const';
import { ProductInvoicePreview, PricePreviewInputParams, ProductType, FeatureTypes } from '@/components/Billing/billing-types';
import { getReadableAmountInDollars } from '@/utils/helpers';
// @ts-ignore
import BillingUsageLayoutDataProvider from '@/views/BillingUsageLayoutDataProvider.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BillingUsageLayoutDataInitializer',
  setup(__props) {

const {
	entitlements,
	usePlans,
	billingPeriod,
	subscriptions,
	usePricingPreview,
	useSubscriptionByProductType,
	usePlanById,
	getEntitlements,
	getPaymentMethod,
	getBillingPreview,
	useEntitlementById,
	getAccountSubscriptions,
	// getProductAutomatedSubscriptionChanges,
} = useBilling();
const toast = getCurrentInstance()?.proxy.$toast;
const isLoading = ref(false);
const isLoadingPrices = ref(false);
const showErrorMessage = ref(false);
const entitlementById = useEntitlementById();
const planEndDate = ref();
const renewReadableDate = computed(() => moment(planEndDate.value).format(DATE_FORMAT_DISPLAY));
const planById = usePlanById();
// Team features
const teamFeaturePlanId = computed(() =>
	useSubscriptionByProductType.value(ProductType.TeamFeatures)
		? BillingService.getRelevantPlanIdForSubscription(useSubscriptionByProductType.value(ProductType.TeamFeatures))
		: ''
);
const teamFeaturesPricePreviewInputParams = computed(() => ({
	planId: teamFeaturePlanId.value,
	billingPeriod: billingPeriod.value,
	productId: ProductType.TeamFeatures,
}));
// Customer features
const customerFeatureSubscription = computed(() => useSubscriptionByProductType.value(ProductType.CustomerFeatures));
const customerFeaturePlanId = computed(() =>
	customerFeatureSubscription.value ? BillingService.getRelevantPlanIdForSubscription(customerFeatureSubscription.value) : ''
);
const customerFeatureQuantity = computed(() => {
	const subscription = useSubscriptionByProductType.value(ProductType.CustomerFeatures);
	const entitlement = entitlementById.value(FeatureTypes.CustomerTasks);
	if (!entitlement || !subscription) {
		return 0;
	}
	return BillingService.getCustomerFeatureUsageLimit(subscription, false, entitlement);
});
const customerFeaturesPricePreviewInputParams = computed(() => ({
	billingPeriod: billingPeriod.value,
	planId: customerFeaturePlanId.value,
	productId: ProductType.CustomerFeatures,
	unitQuantity: customerFeatureQuantity.value,
}));
// Automation
const automationQuantity = computed(() => {
	const entitlement = entitlementById.value(FeatureTypes.AutomationTasks);
	const subscription = useSubscriptionByProductType.value(ProductType.Automation);
	if (!subscription || isEmpty(usePlans.value) || !entitlement || BillingService.isEntitlementUnlimited(entitlement)) {
		return 0;
	}
	return BillingService.getSubscriptionSliderValue(subscription, planById.value, false, entitlement);
});
const automationPricePreviewInputParams = computed(() => ({
	productId: ProductType.Automation,
	billingPeriod: billingPeriod.value,
	unitQuantity: automationQuantity.value,
}));

// Ply credits
const plyCreditsQuantity = computed(() => {
	const entitlement = entitlementById.value(FeatureTypes.Credits);
	const subscription = useSubscriptionByProductType.value(ProductType.Credits);
	if (!subscription || isEmpty(usePlans.value) || !entitlement || BillingService.isEntitlementUnlimited(entitlement)) {
		return 0;
	}
	return BillingService.getSubscriptionSliderValue(subscription, planById.value, false, entitlement);
});
const plyCreditsPricePreviewInputParams = computed(() => ({
	productId: ProductType.Credits,
	billingPeriod: billingPeriod.value,
	unitQuantity: plyCreditsQuantity.value,
}));

// Prices
const getProductPricePreview = (pricePreview: ProductInvoicePreview | undefined) => BillingService.getProductPricePreview(pricePreview);
const teamFeaturesPrices = computed(() =>
	getProductPricePreview(usePricingPreview.value(BillingService.buildPricePreviewInput(teamFeaturesPricePreviewInputParams.value)))
);
const customerFeaturesPrices = computed(() =>
	getProductPricePreview(usePricingPreview.value(BillingService.buildPricePreviewInput(customerFeaturesPricePreviewInputParams.value)))
);
const automationPrices = computed(() =>
	getProductPricePreview(usePricingPreview.value(BillingService.buildPricePreviewInput(automationPricePreviewInputParams.value)))
);
const plyCreditsPrices = computed(() =>
	getProductPricePreview(usePricingPreview.value(BillingService.buildPricePreviewInput(plyCreditsPricePreviewInputParams.value)))
);
const upComingInvoicePrice = computed(() => {
	const teamFeaturesUpComingInvoice = teamFeaturesPrices.value.upComingInvoice;
	const customerFeaturesUpComingInvoice = customerFeaturesPrices.value.upComingInvoice;
	const automationUpComingInvoice = automationPrices.value.upComingInvoice;
	const plyCreditsUpComingInvoice = plyCreditsPrices.value.upComingInvoice;
	return teamFeaturesUpComingInvoice + customerFeaturesUpComingInvoice + automationUpComingInvoice + plyCreditsUpComingInvoice;
});
const readableRenewableAmount = computed(() => getReadableAmountInDollars(upComingInvoicePrice.value));

const onReloadData = () => {
	showErrorMessage.value = false;
	return initializeData();
};
const isEnterprisePlan = (planId: string) => (planById.value(planId) ? BillingService.isEnterprisePlan(planById.value(planId)) : false);
const getPricePreview = async ({ planId, productId, unitQuantity, billingPeriod }: PricePreviewInputParams) => {
	if (planId && isEnterprisePlan(planId)) {
		return;
	}
	let res: ProductInvoicePreview = {} as ProductInvoicePreview;
	res = await getBillingPreview({
		pricePreviewInput: BillingService.buildPricePreviewInput({ productId, planId, unitQuantity, billingPeriod }),
	});
	const subscriptionEndRange = res.preview_subscription.billing_period_range.end;
	if (subscriptionEndRange) {
		planEndDate.value = subscriptionEndRange;
	}
	return res;
};

const getTeamFeaturesPrice = async () => getPricePreview(teamFeaturesPricePreviewInputParams.value);
const getCustomerFeaturesPrice = async () => getPricePreview(customerFeaturesPricePreviewInputParams.value);
const getAutomationPrice = async () => getPricePreview(automationPricePreviewInputParams.value);
const getPlyCreditsPrice = async () => getPricePreview(plyCreditsPricePreviewInputParams.value);

// const getTeamFeaturesAutomatedSubscriptionChanges = async () =>
// 	getProductAutomatedSubscriptionChanges({ productId: ProductType.TeamFeatures });
// const getCustomerFeaturesAutomatedSubscriptionChanges = async () =>
// 	getProductAutomatedSubscriptionChanges({ productId: ProductType.CustomerFeatures });
// const getAutomationAutomatedSubscriptionChanges = async () => getProductAutomatedSubscriptionChanges({ productId: ProductType.Automation });
// const getPlyCreditsAutomatedSubscriptionChanges = async () => getProductAutomatedSubscriptionChanges({ productId: ProductType.Credits });

const onError = () => {
	toast?.error('Error occurred while fetching data. Please try again later');
	showErrorMessage.value = true;
};
const getAllPricePreviews = () =>
	Promise.all([getAutomationPrice(), getPlyCreditsPrice(), getTeamFeaturesPrice(), getCustomerFeaturesPrice()]);
const initializeData = async () => {
	try {
		isLoading.value = true;
		await Promise.all([
			getEntitlements({}),
			getPaymentMethod(),
			getEntitlements({}),
			getAccountSubscriptions(),
			// getAutomationAutomatedSubscriptionChanges(),
			// getPlyCreditsAutomatedSubscriptionChanges(),
			// getTeamFeaturesAutomatedSubscriptionChanges(),
			// getCustomerFeaturesAutomatedSubscriptionChanges(),
		]);
		fetchPrices();
	} catch (e) {
		onError();
	} finally {
		isLoading.value = false;
	}
};
const didFinishLoadingPricePreviewsRequiredData = computed(
	() => !isEmpty(subscriptions.value) && !isEmpty(usePlans.value) && !isEmpty(entitlements.value)
);
const fetchPrices = async () => {
	try {
		isLoadingPrices.value = true;
		await getAllPricePreviews();
	} catch (e) {
		onError();
	} finally {
		isLoadingPrices.value = false;
	}
};
initializeData();

return { __sfc: true,entitlements, usePlans, billingPeriod, subscriptions, usePricingPreview, useSubscriptionByProductType, usePlanById, getEntitlements, getPaymentMethod, getBillingPreview, useEntitlementById, getAccountSubscriptions, toast, isLoading, isLoadingPrices, showErrorMessage, entitlementById, planEndDate, renewReadableDate, planById, teamFeaturePlanId, teamFeaturesPricePreviewInputParams, customerFeatureSubscription, customerFeaturePlanId, customerFeatureQuantity, customerFeaturesPricePreviewInputParams, automationQuantity, automationPricePreviewInputParams, plyCreditsQuantity, plyCreditsPricePreviewInputParams, getProductPricePreview, teamFeaturesPrices, customerFeaturesPrices, automationPrices, plyCreditsPrices, upComingInvoicePrice, readableRenewableAmount, onReloadData, isEnterprisePlan, getPricePreview, getTeamFeaturesPrice, getCustomerFeaturesPrice, getAutomationPrice, getPlyCreditsPrice, onError, getAllPricePreviews, initializeData, didFinishLoadingPricePreviewsRequiredData, fetchPrices, BillingUsageLayoutDataProvider }
}

})